import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface Theme {
  id: string;
  name: string;
  description: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    border: string;
    success: string;
    warning: string;
    error: string;
    header: string;
    headerText: string;
  };
  shadows: {
    card: string;
    elevated: string;
  };
  borderRadius: {
    small: string;
    medium: string;
    large: string;
  };
}

export const themes: Record<string, Theme> = {
  default: {
    id: 'default',
    name: 'Professional Blue',
    description: 'Clean and professional with blue accents',
    colors: {
      primary: '#3b82f6',
      secondary: '#6b7280',
      accent: '#0ea5e9',
      background: '#f9fafb',
      surface: '#ffffff',
      text: '#1f2937',
      textSecondary: '#6b7280',
      border: '#e5e7eb',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      header: '#1e3a8a',
      headerText: '#ffffff',
    },
    shadows: {
      card: '0 1px 3px rgba(0, 0, 0, 0.1)',
      elevated: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
    borderRadius: {
      small: '4px',
      medium: '8px',
      large: '16px',
    },
  },
  dark: {
    id: 'dark',
    name: 'Midnight Pro',
    description: 'Dark theme for reduced eye strain',
    colors: {
      primary: '#3b82f6',
      secondary: '#9ca3af',
      accent: '#06b6d4',
      background: '#111827',
      surface: '#1f2937',
      text: '#f9fafb',
      textSecondary: '#d1d5db',
      border: '#374151',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#f87171',
      header: '#030712',
      headerText: '#f9fafb',
    },
    shadows: {
      card: '0 1px 3px rgba(0, 0, 0, 0.3)',
      elevated: '0 4px 12px rgba(0, 0, 0, 0.4)',
    },
    borderRadius: {
      small: '4px',
      medium: '8px',
      large: '16px',
    },
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean Breeze',
    description: 'Calming blues and teals inspired by the ocean',
    colors: {
      primary: '#0891b2',
      secondary: '#64748b',
      accent: '#06b6d4',
      background: '#f0f9ff',
      surface: '#ffffff',
      text: '#0f172a',
      textSecondary: '#475569',
      border: '#cbd5e1',
      success: '#059669',
      warning: '#d97706',
      error: '#dc2626',
      header: '#0e7490',
      headerText: '#ffffff',
    },
    shadows: {
      card: '0 1px 3px rgba(14, 116, 144, 0.1)',
      elevated: '0 4px 12px rgba(14, 116, 144, 0.15)',
    },
    borderRadius: {
      small: '6px',
      medium: '12px',
      large: '20px',
    },
  },
  forest: {
    id: 'forest',
    name: 'Forest Green',
    description: 'Natural greens for an eco-friendly feel',
    colors: {
      primary: '#059669',
      secondary: '#6b7280',
      accent: '#10b981',
      background: '#f0fdf4',
      surface: '#ffffff',
      text: '#064e3b',
      textSecondary: '#374151',
      border: '#d1fae5',
      success: '#22c55e',
      warning: '#eab308',
      error: '#dc2626',
      header: '#047857',
      headerText: '#ffffff',
    },
    shadows: {
      card: '0 1px 3px rgba(4, 120, 87, 0.1)',
      elevated: '0 4px 12px rgba(4, 120, 87, 0.15)',
    },
    borderRadius: {
      small: '4px',
      medium: '8px',
      large: '16px',
    },
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset Glow',
    description: 'Warm oranges and purples like a beautiful sunset',
    colors: {
      primary: '#ea580c',
      secondary: '#78716c',
      accent: '#f97316',
      background: '#fef7f0',
      surface: '#ffffff',
      text: '#431407',
      textSecondary: '#57534e',
      border: '#fed7aa',
      success: '#16a34a',
      warning: '#eab308',
      error: '#dc2626',
      header: '#c2410c',
      headerText: '#ffffff',
    },
    shadows: {
      card: '0 1px 3px rgba(194, 65, 12, 0.1)',
      elevated: '0 4px 12px rgba(194, 65, 12, 0.15)',
    },
    borderRadius: {
      small: '6px',
      medium: '10px',
      large: '18px',
    },
  },
  royal: {
    id: 'royal',
    name: 'Royal Purple',
    description: 'Elegant purples for a premium experience',
    colors: {
      primary: '#7c3aed',
      secondary: '#6b7280',
      accent: '#8b5cf6',
      background: '#faf7ff',
      surface: '#ffffff',
      text: '#3c1361',
      textSecondary: '#4b5563',
      border: '#e9d5ff',
      success: '#059669',
      warning: '#d97706',
      error: '#dc2626',
      header: '#5b21b6',
      headerText: '#ffffff',
    },
    shadows: {
      card: '0 1px 3px rgba(91, 33, 182, 0.1)',
      elevated: '0 4px 12px rgba(91, 33, 182, 0.15)',
    },
    borderRadius: {
      small: '8px',
      medium: '12px',
      large: '20px',
    },
  },
};

interface ThemeContextType {
  currentTheme: Theme;
  setTheme: (themeId: string) => void;
  availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentThemeId, setCurrentThemeId] = useState<string>(() => {
    return localStorage.getItem('storage-tank-theme') || 'default';
  });

  const currentTheme = themes[currentThemeId] || themes.default;
  const availableThemes = Object.values(themes);

  const setTheme = (themeId: string) => {
    if (themes[themeId]) {
      setCurrentThemeId(themeId);
      localStorage.setItem('storage-tank-theme', themeId);
    }
  };

  // Apply theme CSS variables to the document
  useEffect(() => {
    const root = document.documentElement;
    
    // Apply all theme colors as CSS variables
    Object.entries(currentTheme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // Apply shadows
    Object.entries(currentTheme.shadows).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-${key}`, value);
    });

    // Apply border radius
    Object.entries(currentTheme.borderRadius).forEach(([key, value]) => {
      root.style.setProperty(`--border-radius-${key}`, value);
    });

    // Apply theme ID as a data attribute
    root.setAttribute('data-theme', currentTheme.id);
  }, [currentTheme]);

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme, availableThemes }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};